import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Field, SelectField } from "./DealsAdmin";

const EMPTY = {
  card_name: "", issuer: "", network: "Visa", annual_fee: 0, welcome_bonus: "", bonus_value_text: "",
  min_spend_requirement: "", bonus_categories: [], lounge_access: false, hotel_benefits: [], airline_benefits: [],
  foreign_transaction_fee: 0, transfer_partners: [], affiliate_url: "", apply_button_text: "Apply Now",
  disclosure_text: "JetsetLess may earn a commission when you apply through our links. Terms apply.",
  card_image: "", badge_text: "", is_active: true, editorial_rating: 4.5, featured_benefits: [], shortcode_key: "",
};

export default function CardsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [usage, setUsage] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/cards?limit=200").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const errors = [];
      if (!editing.card_name) errors.push("card_name");
      if (!editing.issuer) errors.push("issuer");
      if (!editing.affiliate_url) errors.push("affiliate_url");
      if (errors.length) return toast.error("Missing: " + errors.join(", "));
      const body = { ...editing, annual_fee: Number(editing.annual_fee), foreign_transaction_fee: Number(editing.foreign_transaction_fee), editorial_rating: Number(editing.editorial_rating) };
      if (editing.id) await api.put(`/cards/${editing.id}`, body);
      else await api.post("/cards", body);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => { if (!confirm("Delete card?")) return; await api.delete(`/cards/${id}`); load(); };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/cards/csv-upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} cards. ${data.errors.length} errors.`);
      load();
    } catch { toast.error("CSV upload failed"); }
  };

  const showUsage = async (id) => {
    const { data } = await api.get(`/cards/${id}/usage`);
    setUsage({ id, data });
  };

  const asList = (v) => Array.isArray(v) ? v.join(", ") : (v || "");
  const toList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  return (
    <div className="p-8 md:p-10">
      <div className="flex justify-between mb-6">
        <div><div className="overline text-[#B4533C]">Credit Cards</div><h1 className="font-serif text-4xl mt-1">Affiliate Manager</h1></div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".csv" onChange={uploadCsv} className="hidden" data-testid="csv-file-input" />
          <button onClick={() => fileRef.current?.click()} className="border border-[#D6CFC4] px-5 py-2.5 text-sm" data-testid="upload-csv-btn">Upload CSV</button>
          <button onClick={() => setEditing({ ...EMPTY })} className="bg-[#1B3B36] text-white px-5 py-2.5 text-sm" data-testid="new-card-btn">+ New Card</button>
        </div>
      </div>
      <div className="text-xs font-mono text-[#4A4742] mb-4">
        CSV columns: card_name, issuer, network, annual_fee, welcome_bonus, bonus_value_text, min_spend_requirement, bonus_categories, lounge_access, hotel_benefits, airline_benefits, foreign_transaction_fee, transfer_partners, affiliate_url, apply_button_text, card_image, badge_text, is_active, editorial_rating, featured_benefits, shortcode_key. Use <code>|</code> to separate list items.
      </div>
      <div className="border border-[#D6CFC4]">
        <table className="w-full admin-table">
          <thead className="bg-[#EFECE5]"><tr><th className="p-3 text-left">Card</th><th className="p-3 text-left">Issuer</th><th className="p-3 text-left">AF</th><th className="p-3 text-left">Lounge</th><th className="p-3 text-left">Active</th><th className="p-3 text-left">Rating</th><th></th></tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-[#D6CFC4]" data-testid={`card-row-${c.id}`}>
                <td className="p-3"><button onClick={() => setEditing(c)} className="hover:text-[#B4533C]">{c.card_name}</button></td>
                <td className="p-3 text-xs">{c.issuer}</td>
                <td className="p-3 text-xs font-mono">${c.annual_fee}</td>
                <td className="p-3 text-xs">{c.lounge_access ? "✓" : "—"}</td>
                <td className="p-3 text-xs">{c.is_active ? "✓" : "—"}</td>
                <td className="p-3 text-xs font-mono">{c.editorial_rating}</td>
                <td className="p-3 text-right flex gap-2 justify-end">
                  <button onClick={() => showUsage(c.id)} className="text-[#1B3B36] text-xs">Usage</button>
                  <button onClick={() => del(c.id)} className="text-[#B4533C] text-xs">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {usage && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-6">
          <div className="bg-white border border-[#D6CFC4] p-8 w-full max-w-xl">
            <h2 className="font-serif text-2xl mb-3">Card usage</h2>
            <p className="text-sm text-[#4A4742] mb-4">Used in {usage.data.count} articles</p>
            <ul className="text-sm space-y-1 max-h-60 overflow-y-auto">
              {usage.data.articles.map((a) => <li key={a.id}>{a.title} <span className="font-mono text-xs text-[#4A4742]">({a.status})</span></li>)}
            </ul>
            <button onClick={() => setUsage(null)} className="mt-5 border border-[#D6CFC4] px-4 py-2 text-sm">Close</button>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-6 overflow-y-auto">
          <div className="bg-white border border-[#D6CFC4] p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="font-serif text-2xl mb-4">{editing.id ? "Edit" : "New"} Card</h2>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Card name *" v={editing.card_name} k="card_name" e={setEditing} />
              <Field label="Issuer *" v={editing.issuer} k="issuer" e={setEditing} />
              <SelectField label="Network" v={editing.network} k="network" e={setEditing} opts={["Visa","Mastercard","Amex","Discover"]} />
              <Field label="Annual fee $" v={editing.annual_fee} k="annual_fee" e={setEditing} type="number" />
              <Field label="Affiliate URL *" v={editing.affiliate_url} k="affiliate_url" e={setEditing} />
              <Field label="Apply button text" v={editing.apply_button_text} k="apply_button_text" e={setEditing} />
              <Field label="Welcome bonus" v={editing.welcome_bonus} k="welcome_bonus" e={setEditing} />
              <Field label="Bonus value text" v={editing.bonus_value_text} k="bonus_value_text" e={setEditing} />
              <Field label="Min spend" v={editing.min_spend_requirement} k="min_spend_requirement" e={setEditing} />
              <Field label="Foreign txn fee %" v={editing.foreign_transaction_fee} k="foreign_transaction_fee" e={setEditing} type="number" />
              <Field label="Card image URL" v={editing.card_image} k="card_image" e={setEditing} />
              <Field label="Badge text" v={editing.badge_text} k="badge_text" e={setEditing} />
              <Field label="Editorial rating 0-5" v={editing.editorial_rating} k="editorial_rating" e={setEditing} type="number" />
              <Field label="Shortcode key" v={editing.shortcode_key} k="shortcode_key" e={setEditing} />
              <Field label="Start date (ISO)" v={editing.start_date} k="start_date" e={setEditing} />
              <Field label="End date (ISO)" v={editing.end_date} k="end_date" e={setEditing} />
            </div>
            <div className="grid md:grid-cols-2 gap-3 mt-3">
              {["bonus_categories","hotel_benefits","airline_benefits","transfer_partners","featured_benefits"].map((k) => (
                <div key={k}><label className="overline">{k.replaceAll("_", " ")} (comma)</label>
                  <input value={asList(editing[k])} onChange={(e) => setEditing({ ...editing, [k]: toList(e.target.value) })} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm" />
                </div>
              ))}
            </div>
            <div className="mt-3"><label className="overline">Disclosure text</label>
              <textarea value={editing.disclosure_text} onChange={(e) => setEditing({ ...editing, disclosure_text: e.target.value })} rows={2} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm" />
            </div>
            <div className="mt-3 flex gap-5">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Active</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.lounge_access} onChange={(e) => setEditing({ ...editing, lounge_access: e.target.checked })} /> Lounge access</label>
            </div>
            <div className="mt-5 flex gap-3">
              <button onClick={save} className="bg-[#1B3B36] text-white px-5 py-2.5 text-sm" data-testid="save-card-btn">Save</button>
              <button onClick={() => setEditing(null)} className="border border-[#D6CFC4] px-5 py-2.5 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
